import { DockviewApi } from 'dockview';
import { TabContext } from '@/entities/tab/model/tab.types';
import { useAddTab } from '@/features/tab/add-tab/model/useAddTab';
import { getTabTitle } from '@/shared/config/route-core';
import {
  appendTabHistoryAtom,
  replaceTabHistoryAtom,
  tabHistoriesAtomFamily,
  updateCurrentHistoryEntryParamsAtom,
} from '@/entities/tab/model/tab.atom';
import { getRouteLevel } from '@/shared/config/route-hierarchy';
import { getDefaultStore } from 'jotai';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

export function useNavigateTab(api: DockviewApi | null) {
  const { addTab } = useAddTab(api);
  const appendHistory = useSetAtom(appendTabHistoryAtom);
  const replaceHistory = useSetAtom(replaceTabHistoryAtom);
  const updateCurrentHistoryParams = useSetAtom(updateCurrentHistoryEntryParamsAtom);

  const navigateTab = useCallback(
    (context: TabContext) => {
      if (!api) return;

      const tabId = context.tabId;

      if (!tabId) return addTab(context)?.focus();

      const targetPanel = api.panels.find((panel) => panel.id === tabId);
      if (!targetPanel) {
        const { ...newContext } = context;
        delete newContext.tabId;
        return addTab(newContext)?.focus();
      }

      const currentContext = (targetPanel.params as TabContext | undefined) ?? {
        path: [],
        params: {},
      };
      updateCurrentHistoryParams({ tabId, params: currentContext.params ?? {} });

      const newContext: Required<TabContext> = {
        ...context,
        tabId,
      };

      const currentLevel = getRouteLevel(currentContext);
      const destLevel = getRouteLevel(newContext);
      // 위계가 높아질 때(목적지 > 현재): replace. 예: 상담시작→상담진행 리다이렉트 시 상담시작 저장 안 함
      // 같거나 낮아질 때: append
      const useReplace = destLevel > currentLevel;

      const store = getDefaultStore();
      const tabHistories = store.get(tabHistoriesAtomFamily(tabId));
      const canReplace = (tabHistories?.histories?.length ?? 0) > 0;

      if (useReplace && canReplace) {
        replaceHistory(newContext);
      } else {
        appendHistory(newContext);
      }

      targetPanel.update({ params: context });
      targetPanel.setTitle(getTabTitle(context));
      targetPanel.focus();
    },
    [api, addTab, appendHistory, replaceHistory, updateCurrentHistoryParams]
  );

  return { navigateTab };
}
